import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { getUser, getAPIBaseURL } from "../services/helpers"
import { getData } from "../services/request"
import LoadingStatValue from "../components/LoadingStatValue"

const EmployerDashboard = () => {
  const user = getUser()
  const [stats, setStats] = useState({
    liveJobs: 0,
    pendingApprovalJobs: 0,
    applicantsInReview: 0,
    unpaidDrafts: 0,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true)
      setError("")
      try {
        const jobsUrl = `${getAPIBaseURL()}/employme/employer/jobs`
        const jobsRes = await getData(jobsUrl, {}, {})

        if (jobsRes.error === "" && jobsRes.result.status === 200) {
          const jobs = jobsRes.result.data?.data || []

          const liveJobs = jobs.filter(j => j.status === "LIVE").length
          const pendingApprovalJobs = jobs.filter(j => j.status === "PENDING_APPROVAL").length
          const unpaidDrafts = jobs.filter(j => j.status === "DRAFT" || j.status === "PENDING_PAYMENT").length

          let applicantsInReview = 0

          try {
            const applicantsUrl = `${getAPIBaseURL()}/employme/employer/applicants`
            const applicantsRes = await getData(applicantsUrl, {}, {})
            if (applicantsRes.error === "" && applicantsRes.result.status === 200) {
              const applications = applicantsRes.result.data?.data || []
              applicantsInReview = applications.filter(a => {
                const s = (a.status || "").toString().toUpperCase()
                return s === "INTERVIEW_IN_PROGRESS" || s === "SUBMITTED" || s === "UNDER_REVIEW"
              }).length
            }
          } catch {
          }

          setStats({
            liveJobs,
            pendingApprovalJobs,
            applicantsInReview,
            unpaidDrafts,
          })
        } else if (jobsRes.result?.status === 401 || jobsRes.result?.status === 403) {
          setError("Please log in again as an employer to see your dashboard.")
        } else {
          setError("Unable to load dashboard summary")
        }
      } catch (e) {
        setError("Unable to load dashboard summary")
      } finally {
        setLoading(false)
      }
    }
    fetchStats()
  }, [])

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Welcome back</h1>
        <p className="mt-1 text-sm text-gray-500">
          {user?.organization || user?.companyName || "Employer"} dashboard
        </p>
        {error && (
          <p className="mt-2 text-xs text-red-600">
            {error}
          </p>
        )}
      </div>

      {/* Job status overview */}
      <div className="grid gap-4 sm:gap-6 md:grid-cols-3">
        <div className="rounded-lg bg-white p-4 sm:p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
              <svg className="h-5 w-5 sm:h-6 sm:w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M4 7h16M4 11h16M4 15h10" />
              </svg>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 sm:text-sm">Live jobs</p>
              <p className="text-xl font-bold text-gray-900 sm:text-2xl">
                {loading ? <LoadingStatValue /> : stats.liveJobs}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-white p-4 sm:p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-lg bg-amber-50 text-amber-700">
              <svg className="h-5 w-5 sm:h-6 sm:w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v4l2 2" />
              </svg>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 sm:text-sm">Pending approval</p>
              <p className="text-xl font-bold text-gray-900 sm:text-2xl">
                {loading ? <LoadingStatValue /> : stats.pendingApprovalJobs}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-white p-4 sm:p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-lg bg-sky-50 text-sky-700">
              <svg className="h-5 w-5 sm:h-6 sm:w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-500 sm:text-sm">Applicants to review</p>
              <p className="text-xl font-bold text-gray-900 sm:text-2xl">
                {loading ? <LoadingStatValue /> : stats.applicantsInReview}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6">
        {/* Jobs pipeline & quick actions */}
        <div className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Jobs pipeline</h2>
              <p className="mt-1 text-sm text-gray-500 max-w-md">
                Track your roles from draft to live and quickly see where applicants are in the hiring process.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:items-end">
              <Link
                to="/employer/jobs/new"
                className="inline-flex items-center justify-center rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-900"
              >
                Create job
              </Link>
              <div className="flex gap-2 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-1">
                  <span className="h-2 w-2 rounded-full bg-blue-500" />
                  Live
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-1">
                  <span className="h-2 w-2 rounded-full bg-amber-500" />
                  Pending approval
                </span>
              </div>
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
              <h3 className="mb-1 text-sm font-semibold text-gray-900">Draft & unpaid jobs</h3>
              <p className="text-xs text-gray-500 mb-3">
                Jobs you started but haven&apos;t paid for or submitted for approval yet.
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {loading ? <LoadingStatValue className="h-9 w-12" /> : stats.unpaidDrafts}
              </p>
            </div>
            <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
              <h3 className="mb-1 text-sm font-semibold text-gray-900">Interviews in progress</h3>
              <p className="text-xs text-gray-500 mb-3">
                Candidates currently completing one-question-at-a-time video interviews.
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {loading ? <LoadingStatValue /> : stats.applicantsInReview}
              </p>
            </div>
            <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
              <h3 className="mb-1 text-sm font-semibold text-gray-900">Jobs awaiting admin review</h3>
              <p className="text-xs text-gray-500 mb-3">
                Paid jobs that still need admin approval before going live.
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {loading ? <LoadingStatValue className="h-9 w-12" /> : stats.pendingApprovalJobs}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EmployerDashboard

